import React from 'react';
import { Check, Globe, MessageCircle, Plug, Send, Smartphone } from 'lucide-react';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const METHOD_ICONS = [Globe, Smartphone, MessageCircle, Send, Plug] as const;

const EricaAskAnywhere: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];

  return (
    <section id="channels" className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {c.channels.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{c.channels.subtitle}</p>
        </div>

        <div className="mt-12 grid items-start gap-10 lg:grid-cols-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-0 -z-10 rounded-[36px] bg-gradient-to-br from-slate-200/70 via-white to-white blur-2xl opacity-80" />
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
                <div className="text-sm font-semibold text-slate-900">{c.channels.exampleTitle}</div>
                <div className="text-xs font-semibold text-slate-500">{c.channels.note}</div>
              </div>

              <div className="space-y-3 bg-white p-5">
                <div className="max-w-[92%] rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
                  <div className="text-xs font-semibold text-slate-500">{c.channels.exampleYou}</div>
                  <div className="mt-2 leading-relaxed">{c.channels.exampleQuestion}</div>
                </div>

                <div className="ml-auto max-w-[92%] rounded-2xl bg-slate-900 p-4 text-sm text-white shadow-sm">
                  <div className="text-xs font-semibold text-slate-200">ERICA</div>
                  <div className="mt-2 leading-relaxed">{c.channels.exampleAnswer}</div>
                  <div className="mt-3 rounded-xl bg-white/10 px-3 py-2 text-xs text-slate-100">
                    {c.channels.exampleFooter}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <ul className="space-y-3">
              {c.channels.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-slate-900 shadow-sm ring-1 ring-slate-200">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="leading-relaxed">{bullet}</span>
                </li>
              ))}
            </ul>

            <div>
              <div className="text-sm font-semibold text-slate-900">{c.channels.methodsTitle}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {c.channels.methods.map((method, idx) => {
                  const Icon = METHOD_ICONS[idx] ?? Plug;
                  return (
                    <span
                      key={method}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      <Icon className="h-3.5 w-3.5 text-slate-900" />
                      {method}
                    </span>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
            >
              {c.nav.requestDemo}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EricaAskAnywhere;
